import { sendGTMEvent } from '@next/third-parties/google';

import { useCallback, useEffect, useRef } from 'react';

import { LocalStorageKey } from '@nocowanie/core/enums';
import { commonHelpers } from '@nocowanie/core/helpers';
import {
    DataLayerEventName,
    DataLayerOriginEnum,
    DataLayerPageTypeEnum,
    DataLayerSearchResultsEvent,
} from '@nocowanie/core/models';

import {
    commonUiStateSlice,
    listingStateSlice,
    searchStateSlice,
    useAppSelector,
} from '@app/app-state';
import { AMENITIES_LABELS, PROPERTY_TYPE_LABELS, RATING_LABELS } from '@app/consts';

import { usePageDataLayerEvents } from './usePageDataLayerEvents';
import { usePagePath } from './usePagePath';

const getStorageData = ():
    | { keyword: string | undefined; origin: DataLayerOriginEnum }
    | undefined => {
    const lsItem = localStorage.getItem(LocalStorageKey.DataLayerSearchResults);

    if (!lsItem || !commonHelpers.isJSON(lsItem)) return undefined;

    const { fraza: keyword, zrodlo: origin } = JSON.parse(lsItem);

    localStorage.removeItem(LocalStorageKey.DataLayerSearchResults);

    if (origin && [DataLayerOriginEnum.Home, DataLayerOriginEnum.SearchRefine].includes(origin)) {
        return { keyword: keyword ?? undefined, origin };
    }

    return undefined;
};

const calcNights = (from: string, to: string): number => {
    return Math.ceil((new Date(to).getTime() - new Date(from).getTime()) / (1000 * 3600 * 24));
};

const calcHowLongBefore = (checkIn: string | null): string | undefined => {
    if (!checkIn) return undefined;

    const today = new Date().toISOString().split('T')[0];
    const days = calcNights(today, checkIn);

    if (days < 1) return '0d';
    if (days === 1) return '1d';
    if (days <= 3) return '3d';
    if (days <= 7) return '7d';
    if (days <= 14) return '14d';
    if (days <= 21) return '21d';

    return 'other';
};

const mapPropertyTypes = (types: string[] | undefined): string | undefined => {
    if (!types?.length) return undefined;

    const labels = types
        .map(id => PROPERTY_TYPE_LABELS.find(pt => pt.value === Number(id))?.label)
        .filter(Boolean)
        .join(';');

    return labels || undefined;
};

const mapAmenities = (amenityIds: string[] | undefined): string | undefined => {
    if (!amenityIds?.length) return undefined;

    const labels = amenityIds
        .map(id => AMENITIES_LABELS.find(a => a.value === Number(id))?.label)
        .filter(Boolean)
        .join(';');

    return labels || undefined;
};

const mapRating = (rating: number | null | undefined): string | undefined => {
    if (!rating) return undefined;

    return RATING_LABELS.find(r => r.value === rating)?.label;
};

export function useDataLayerListingEvents() {
    const pagePath = usePagePath();
    const searchCriteria = useAppSelector(searchStateSlice.selectors.selectSearchCriteria);
    const filters = useAppSelector(searchStateSlice.selectors.selectSearchFilters);
    const hasSearchChanged = useAppSelector(searchStateSlice.selectors.selectHasSearchChanged);
    const {
        data: { itemCount },
    } = useAppSelector(listingStateSlice.selectors.selectListingMeta);
    const isPageDataLayerEventSent = useAppSelector(
        commonUiStateSlice.selectors.selectIsPageDataLayerSent,
    );
    const pendingSearchResultsEventRef = useRef<DataLayerSearchResultsEvent | null>(null);

    usePageDataLayerEvents({
        pageType: DataLayerPageTypeEnum.Search,
        pagePath,
    });

    useEffect(() => {
        if (!isPageDataLayerEventSent || !pendingSearchResultsEventRef.current) {
            return;
        }

        sendGTMEvent(pendingSearchResultsEventRef.current);
        pendingSearchResultsEventRef.current = null;
    }, [isPageDataLayerEventSent]);

    const sendSearchResultsDataLayerEvent = useCallback(() => {
        const { checkInDate, checkOutDate, guests, geoId, location, flexDates } = searchCriteria;
        const { propertyType, amenities, rating } = filters;

        const storageData = hasSearchChanged ? undefined : getStorageData();
        const origin = hasSearchChanged
            ? DataLayerOriginEnum.SearchRefine
            : (storageData?.origin ?? DataLayerOriginEnum.Other);

        const nights =
            checkInDate && checkOutDate ? calcNights(checkInDate, checkOutDate) : undefined;

        const eventData: DataLayerSearchResultsEvent = {
            event: DataLayerEventName.SearchResults,
            pageDomain: 'nocowanie.pl',
            origin,
            searchOutputType: 'list',
            searchOutputCount: itemCount,
            searchKeyword: storageData?.keyword ?? location,
            searchLocation: location,
            searchLocationID: geoId ?? undefined,
            searchHowLongBefore: calcHowLongBefore(checkInDate),
            searchDateFrom: checkInDate ?? undefined,
            searchDateTo: checkOutDate ?? undefined,
            searchNights: nights,
            searchAdults: guests.adultsCount,
            searchKids: guests.childrenCount || undefined,
            searchDestType: mapPropertyTypes(propertyType),
            searchFacilities: mapAmenities(amenities),
            searchRank: mapRating(rating),
            searchFlexibility: flexDates ?? 0,
        };

        if (!isPageDataLayerEventSent) {
            pendingSearchResultsEventRef.current = eventData;
            return;
        }

        sendGTMEvent(eventData);
    }, [searchCriteria, filters, itemCount, hasSearchChanged, isPageDataLayerEventSent]);

    return { sendSearchResultsDataLayerEvent };
}
